// CourseCard.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import './CourseCard.css';
import { PiBookOpenTextThin } from "react-icons/pi";
import { GoClock } from "react-icons/go";
import { CiCalendar } from "react-icons/ci";
import { FaHeart, FaRegHeart } from "react-icons/fa";

interface Course {
  id: number;
  image: string;
  imageClass?: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced';
  price: string;
  title: string;
  description: string;
  authorLabel: string;
  authorLocation: string;
  progressFilled?: number; // out of 10 (only for my-courses)
  progressText?: string; // (only for my-courses)
  completedCount?: number; // (only for my-courses)
  totalCount?: number;
  durationMinutes?: number; // total course duration in minutes
  dueDate?: string | undefined; // expected format "dd-mm-yyyy" (only for my-courses, can be undefined)
  totalModules?: number; // total modules (for browse/wishlist)
  totalHours?: number; // total hours (for browse/wishlist)
  isCompleted?: boolean; // for showing certificate download (only for my-courses)
  is_enrolled?: boolean; // whether the course is enrolled
  displayMode: 'my-courses' | 'browse' | 'wishlist'; // display mode
  userRole?: 'trainee' | 'trainer'; // to determine navigation route
}

// Function to get CSS class based on level
const getLevelClass = (level: string): string => {
  const levelLower = level.toLowerCase();
  if (levelLower === 'beginner') return 'log-in-home-beginner';
  if (levelLower === 'intermediate') return 'log-in-home-intermediate';
  if (levelLower === 'advanced') return 'log-in-home-advanced';
  return 'log-in-home-beginner'; // default
};

const formatDuration = (minutes: number): string => {
  if (minutes === 0) return `0 mins Spent`;
  if (minutes < 60) return `${Math.round(minutes)} mins Spent`;
  const hrs = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (mins === 0) return `${hrs} ${hrs === 1 ? 'Hour' : 'Hours'} Spent`;
  return `${hrs}h ${mins}m Spent`;
};

const formatDate = (dateStr: string): string => {
  // expecting "dd-mm-yyyy"
  const [dayStr, monthStr, yearStr] = dateStr.split('-');
  const day = Number(dayStr);
  const month = Number(monthStr);
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const monthName = monthNames[month - 1] ?? '';
  return `${day} ${monthName}`;
};

const truncateText = (text: string, maxLength: number = 120): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

interface CourseCardProps {
  course: Course;
  isSaved?: boolean;
  onToggleSave?: (e: React.MouseEvent) => void;
  onEnroll?: (e: React.MouseEvent) => void;
  hideDuration?: boolean; // Hide the duration/time spent stat
}

const CourseCard: React.FC<CourseCardProps> = ({
  course,
  isSaved = false,
  onToggleSave,
  onEnroll,
  hideDuration = false,
}) => {
  const navigate = useNavigate();
  const isMyCourses = course.displayMode === 'my-courses';
  const isBrowseOrWishlist = course.displayMode === 'browse' || course.displayMode === 'wishlist';

  // Format total duration for browse/wishlist mode
  // Expects hours as decimal (e.g., 1.3 = 1 hour 18 mins)
  const formatTotalDuration = (hours?: number): string => {
    if (!hours || hours === 0) return '0 mins';

    // Convert hours to total minutes
    const totalMinutes = Math.round(hours * 60);

    // If less than 60 minutes, show only minutes
    if (totalMinutes < 60) {
      return `${totalMinutes} mins`;
    }

    // If 60+ minutes, show hours and minutes
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;

    if (mins === 0) {
      return hrs === 1 ? `${hrs} hr` : `${hrs} hrs`;
    }

    return `${hrs} hr ${mins} mins`;
  };

  const handleCardClick = () => {
    // Determine the route based on user role
    const baseRoute = course.userRole === 'trainer' ? '/trainer' : '/trainee';

    // Determine source label based on displayMode for breadcrumb
    let sourceLabel = 'My Courses'; // default
    if (course.displayMode === 'browse') {
      sourceLabel = 'Explore Courses';
    } else if (course.displayMode === 'wishlist') {
      sourceLabel = 'Wishlist';
    }

    navigate(`${baseRoute}/course/${course.id}`, {
      state: { sourceLabel }
    });
  };

  return (
    <div className="log-in-home-course-card" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
      <div className={`log-in-home-course-image ${course.imageClass ?? ''}`}>
        <img src={course.image} alt={course.title} />
      </div>
      <div className="log-in-home-course-content">
        <div className="log-in-home-course-header">
          <span className={`log-in-home-course-level ${getLevelClass(course.level)}`}>{course.level}</span>
          {course.displayMode !== 'browse' && (
            <span className="log-in-home-course-price">{course.price}</span>
          )}
        </div>
        <h3>{course.title}</h3>
        <p>{truncateText(course.description)}</p>
        <div className="log-in-home-course-author">
          <div className="log-in-home-author-info">
            <span>{course.authorLocation}</span>
            {isMyCourses && course.isCompleted && (
              <span className="log-in-home-certificate-text">Download Certification</span>
            )}
          </div>

          {/* Show Continue button for enrolled courses only in My Courses mode */}
          {course.is_enrolled && course.displayMode === 'my-courses' ? (
            <button className="log-in-home-continue-btn">
              Continue
            </button>
          ) : (
            <>
              {/* Browse mode: Show save button and optionally enroll button for non-enrolled courses */}
              {course.displayMode === 'browse' && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {onEnroll && (
                    <button
                      className="log-in-home-continue-btn"
                      onClick={onEnroll}
                      style={{
                        flex: 1,
                        backgroundColor: 'var(--primary-color, #5966A8)',
                        color: 'white',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      Enroll Now
                    </button>
                  )}
                  {onToggleSave && (
                    <button
                      className={`course-card-heart-btn ${isSaved ? 'course-card-heart-saved' : ''}`}
                      onClick={onToggleSave}
                      title={isSaved ? 'Remove from wishlist' : 'Add to wishlist'}
                    >
                      {isSaved ? <FaHeart /> : <FaRegHeart />}
                    </button>
                  )}
                </div>
              )}

              {/* Wishlist mode: Show enroll and heart icon for non-enrolled courses */}
              {course.displayMode === 'wishlist' && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {onEnroll && (
                    <button
                      className="log-in-home-continue-btn"
                      onClick={onEnroll}
                      style={{
                        flex: 1,
                        backgroundColor: 'var(--primary-color, #5966A8)',
                        color: 'white',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      Enroll Now
                    </button>
                  )}
                  {onToggleSave && (
                    <button
                      className="course-card-heart-btn course-card-heart-saved"
                      onClick={onToggleSave}
                      title="Remove from wishlist"
                    >
                      <FaHeart />
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Progress blocks for My Courses */}
        {isMyCourses && (
          <div className="log-in-home-course-progress">
            <div className="log-in-home-progress-dots">
              {[...Array(course.totalCount ?? 0)].map((_, i) => {
                const isFilled = i < (course.completedCount ?? 0);
                const completionPercentage = course.totalCount
                  ? ((course.completedCount ?? 0) / course.totalCount) * 100
                  : 0;
                const filledClass =
                  completionPercentage <= 50
                    ? 'log-in-home-filled'
                    : 'log-in-home-filled log-in-home-green';
                return (
                  <div
                    key={i}
                    className={`log-in-home-dot ${
                      isFilled ? filledClass : ''
                    }`}
                  ></div>
                );
              })}
            </div>
            <span className="log-in-home-progress-text">{course.progressText}</span>
          </div>
        )}

        {/* Info for Browse/Wishlist */}
        {isBrowseOrWishlist && (
          <div className="log-in-home-course-info">
            <span className="log-in-home-course-info-item">
              <span className="log-in-home-course-stat-icon"><PiBookOpenTextThin /></span>
              <span>{course.totalModules ?? 0} lessons</span>
            </span>
            <span className="log-in-home-course-info-item">
              <span className="log-in-home-course-stat-icon"><GoClock /></span>
              <span>{formatTotalDuration(course.totalHours)}</span>
            </span>
          </div>
        )}

        {/* Stats for My Courses */}
        {isMyCourses && (
          <div className="log-in-home-course-stats">
            <span className="log-in-home-course-stat">
              <span className="log-in-home-course-stat-icon"><PiBookOpenTextThin /></span>
              <span>{`${course.completedCount ?? 0}/${course.totalCount ?? 0} Completed`}</span>
            </span>
            {!hideDuration && course.durationMinutes !== undefined && course.durationMinutes > 0 && (
              <span className="log-in-home-course-stat">
                <span className="log-in-home-course-stat-icon"><GoClock /></span>
                <span>{formatDuration(
                  // Calculate estimated time spent based on completed lessons
                  course.totalCount && course.totalCount > 0
                    ? (course.completedCount ?? 0) / course.totalCount * course.durationMinutes
                    : 0
                )}</span>
              </span>
            )}
            {course.dueDate && (
              <span className="log-in-home-course-stat">
                <span className="log-in-home-course-stat-icon"><CiCalendar /></span>
                <span>Due: {formatDate(course.dueDate)}</span>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseCard;